import React, { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadSortedProducts } from "../store/product-list/ProductListActions";
import { Typography, FormControl, Skeleton } from "@mui/material";
import { useIntl } from "react-intl";
import { OVERLAY_TABS } from "../utils/Constants";
import { styles, useStyles } from "./SmartSort.styles";
import DropdownMenu from "./common/DropdownMenu";
import {
  selectIsClusterResponseFetched,
  selectedSmartSortValue,
} from "store/ai-cluster/AiClusterSelectors";
import { isSmartSortRuleId } from "utils/SmartSortUtils";
import { SMART_SORT_RULE_IDS } from "utils/Constants";
import { selectSystemGeneratedRules } from "store/businessRules-list/BusinessRuleslistSelectors";
import { loadSystemGeneratedRules } from "store/businessRules-list/BusinessRulesListActions";
import { useEffect } from "react";
import { setSelectedSmartSort } from "store/ai-cluster/AiClusterAction";
import ButtonComponent from "./common/ButtonComponent";
import { selectOverlay } from "store/overlay/OverlaySelectors";
import AppState from "store/AppState";
import { selectConfigValue } from "store/app-config/AppConfigSelectors";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";

const SmartSort = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { bodyWrap } = styles();
  const dispatch = useDispatch();
  const selectedSmartSort = useSelector(selectedSmartSortValue);
  const selectedOverlay = useSelector(selectOverlay);
  const isClusterRespFetched = useSelector(selectIsClusterResponseFetched);
  const isBoostBuryEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableBoostBury"),
  );
  const systemGeneratedRules = useSelector(selectSystemGeneratedRules);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isUnmanagedProductsSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  ) as boolean;

  useEffect(() => {
    dispatch(loadSystemGeneratedRules());
  }, [dispatch]);

  const showBoostBury = useMemo(
    () =>
      isBoostBuryEnabled === "true" &&
      selectedOverlay === OVERLAY_TABS.SMAI &&
      isClusterRespFetched,
    [isBoostBuryEnabled, selectedOverlay, isClusterRespFetched],
  );

  const sortOptions = useMemo(() => {
    const smartSortRules = systemGeneratedRules
      .filter((rule) => isSmartSortRuleId(rule.ruleId))
      .map((rule) => ({
        label: rule.name,
        value: rule.ruleId,
      }));

    // Filter based on boost/bury visibility
    if (showBoostBury) {
      return smartSortRules;
    } else {
      return smartSortRules.filter(
        (rule) =>
          rule.value !== SMART_SORT_RULE_IDS.BOOSTED &&
          rule.value !== SMART_SORT_RULE_IDS.BURIED,
      );
    }
  }, [systemGeneratedRules, showBoostBury]);

  const handleSmartSortChange = useCallback(
    (event) => {
      dispatch(setSelectedSmartSort(event.target.value));
    },
    [dispatch],
  );

  const applySmartSort = useCallback(() => {
    dispatch(
      loadSortedProducts(
        selectedSmartSort,
        undefined,
        undefined,
        isUnmanagedProductsSupported,
      ),
    );
  }, [dispatch, selectedSmartSort, isUnmanagedProductsSupported]);

  return (
    <div className={classes.root}>
      <div>
        <div className={classes.sortClass}>
          {!systemGeneratedRules.length ? (
            <div>
              <Skeleton variant="rectangular" width={212} height={32} />
            </div>
          ) : (
            <>
              <FormControl sx={bodyWrap}>
                <label>
                  <Typography variant="subHeader">
                    {intl.formatMessage({
                      id: "smartSort.label",
                      defaultMessage: "Smart Sort",
                    })}
                  </Typography>
                </label>
                <DropdownMenu
                  menuId="SmartSortTools"
                  value={selectedSmartSort}
                  changeHandler={handleSmartSortChange}
                  smartSortMenuList={sortOptions}
                />
              </FormControl>
              <ButtonComponent
                color="primary"
                variant="contained"
                disabled={!selectedSmartSort}
                onClick={applySmartSort}
              >
                {intl.formatMessage({
                  id: "smartSortButton.label",
                  defaultMessage: "Move",
                })}
              </ButtonComponent>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SmartSort);
